export function normalizeModel(value) {
  return String(value || "").toUpperCase().replace(/[^A-Z0-9]/g, "");
}

export function customerBadgeLabel(classification, publicLabel = "") {
  if (classification === "VERIFIED CLOSEST CURRENT FIT") return "Closest current fit";
  if (publicLabel === "RECOMMENDED UPGRADE") return "Recommended upgrade";
  if (publicLabel === "SAME-MODEL OPTIONS") return "Same-model options";
  if (publicLabel === "CURRENT MODEL OPTIONS") return "Current model options";
  if (publicLabel === "POS UPGRADE OPTIONS") return "POS upgrade options";
  return "Find your replacement";
}

const DEALER_UPGRADES = Object.freeze({
  SPS520FT: "SAP-630II FT",
  SPS530FT: "SAP-630II FT",
  SPS520RT: "SAP-630II RT",
  SPS530RT: "SAP-630II RT",
  ER650: "SPS-320",
  ER650R: "SPS-345",
  SPS320: "SPS-320",
  SPS340: "SPS-340",
  SPS345: "SPS-345",
  ER180TB: "ER-180U",
});
export function dealerUpgradeFor(model) {
  return DEALER_UPGRADES[normalizeModel(model)] || null;
}

export const TRACKED_EVENTS = Object.freeze([
  "replacement_finder_search",
  "replacement_finder_exact_match",
  "replacement_finder_no_match",
  "replacement_path_open",
  "replacement_comparison_view",
  "replacement_document_click",
  "replacement_programming_click",
  "replacement_intake_start",
]);

function selectedModel(path, exactModel) {
  const normalized = normalizeModel(exactModel);
  const explicit = {
    ER920: "ER-920E",
    ER925: "ER-925E",
    ER940: "ER-940E",
    ER945: "ER-945E",
    ER5200M: "ER-940E",
    ER5240M: "ER-945E",
    ER265B: "ER-265EJ",
    ER390M: "ER-920E",
    NR510B: "NR-510BE",
    NR510RB: "NR-510RBE",
  };
  return explicit[normalized] || null;
}

const BRAND_WORDS = Object.freeze(["CASIO", "SHARP", "SAMSUNG"]);

export function findMatch(query, paths) {
  const normalized = normalizeModel(query);
  if (!normalized) return null;

  const exactCandidates = [];
  for (const path of paths) {
    for (const alias of path.aliases || []) {
      if (normalizeModel(alias) === normalized) exactCandidates.push({ path, alias });
    }
  }
  if (!exactCandidates.length) {
    const family = paths.find((candidate) => (candidate.familyAliases || []).some((alias) => {
      const normalizedAlias = normalizeModel(alias);
      return normalizedAlias.includes(normalized) || normalized.includes(normalizedAlias);
    }));
    // A brand followed by a model we have no record for ("Sharp XE-A137",
    // "Casio PCR-T2300") goes to that brand's path. It is routed, not matched:
    // the path stays NEEDS REVIEW and names no replacement.
    const brand = family || paths.find((candidate) => (candidate.aliases || []).some((alias) => {
      const normalizedAlias = normalizeModel(alias);
      return BRAND_WORDS.includes(normalizedAlias) && normalized.length > normalizedAlias.length && normalized.startsWith(normalizedAlias);
    }));
    if (!brand) return null;
    if (!family) {
      // "Samsung ER-5240" must still find the ER-5240M path before the brand does.
      const brandWord = BRAND_WORDS.find((word) => normalized.startsWith(word));
      const rest = findMatch(normalized.slice(brandWord.length), paths);
      if (rest && rest.matchType === "exact") return rest;
    }
    return {
      path: brand,
      exactModel: null,
      classification: brand.classification,
      displayClassification: customerBadgeLabel(brand.classification, brand.classificationLabel),
      selectedCurrentModel: null,
      showComparison: Boolean(brand.comparison && brand.classification === "VERIFIED CLOSEST CURRENT FIT"),
      matchType: family ? "family" : "brand",
    };
  }

  exactCandidates.sort((a, b) => {
    const aDedicated = (a.path.aliases || []).length <= 2 ? 1 : 0;
    const bDedicated = (b.path.aliases || []).length <= 2 ? 1 : 0;
    return bDedicated - aDedicated;
  });

  const path = exactCandidates[0].path;
  const modelEntry = Object.entries(path.modelClassifications || {}).find(
    ([model]) => normalizeModel(model) === normalized,
  );
  const classification = modelEntry ? modelEntry[1] : path.classification;
  const closest = classification === "VERIFIED CLOSEST CURRENT FIT";
  const impactPrinterVariant = path.id === "replace-er-650-er-650r" && ["ER655", "ER655II"].includes(normalized);
  return {
    path,
    exactModel: modelEntry ? modelEntry[0] : exactCandidates[0].alias,
    classification,
    displayClassification: impactPrinterVariant ? "Find your replacement" : customerBadgeLabel(classification, path.classificationLabel),
    selectedCurrentModel: closest ? selectedModel(path, modelEntry?.[0] || exactCandidates[0].alias) : dealerUpgradeFor(modelEntry?.[0] || exactCandidates[0].alias),
    showComparison: Boolean(closest && path.comparison),
    matchType: "exact",
  };
}

export function buildSearchEvent(query, match) {
  return {
    event: "replacement_finder_search",
    query: String(query || "").slice(0, 60),
    exact_match: Boolean(match),
    legacy_model: match?.exactModel || null,
    path: match?.path?.id || null,
    evidence_classification: match?.classification || null,
    selected_current_model: match?.selectedCurrentModel || null,
    lead: false,
  };
}

function emit(event, payload = {}) {
  const data = { ...payload, event, page: window.location.pathname };
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(data);
  if (window.Shopify?.analytics?.publish) {
    try { window.Shopify.analytics.publish(event, data); } catch (_) {}
  }
  if (window.S4SGA4Forward) window.S4SGA4Forward(event, data);
}

function domPaths(root) {
  return [...root.querySelectorAll("[data-replacement-path]")].map((el) => {
    const modelClassifications = {};
    for (const item of (el.dataset.modelClassifications || "").split("|")) {
      const splitAt = item.indexOf("=");
      if (splitAt > -1) modelClassifications[item.slice(0, splitAt)] = item.slice(splitAt + 1);
    }
    return {
      id: el.dataset.path,
      aliases: (el.dataset.aliases || "").split("|").filter(Boolean),
      familyAliases: (el.dataset.familyAliases || "").split("|").filter(Boolean),
      classification: el.dataset.classification,
      classificationLabel: el.dataset.classificationLabel,
      modelClassifications,
      comparison: el.dataset.hasComparison === "true" ? {} : null,
      element: el,
    };
  });
}

function setExactState(root, match) {
  for (const row of root.querySelectorAll("[data-replacement-path]")) {
    const active = row === match?.path?.element;
    row.hidden = Boolean(match) && !active;
    if (match && !active) row.open = false;
    row.toggleAttribute("data-exact-match", active);
    if (!match) {
      for (const option of row.querySelectorAll("[data-upgrade-model]")) option.hidden = false;
      for (const note of row.querySelectorAll("[data-upgrade-unmatched]")) note.hidden = true;
      delete row.dataset.activeClassification;
      delete row.dataset.selectedCurrentModel;
      const pathHasComparison = row.dataset.hasComparison === "true";
      for (const block of row.querySelectorAll("[data-closest-content]")) block.hidden = !pathHasComparison;
      for (const block of row.querySelectorAll("[data-unresolved-content]")) block.hidden = pathHasComparison;
      const pathBadge = row.querySelector("[data-active-badge]");
      if (pathBadge) {
        pathBadge.textContent = row.dataset.classificationLabel;
        pathBadge.classList.toggle("replacement-path-v2__badge--closest", pathHasComparison);
        pathBadge.classList.toggle("replacement-path-v2__badge--review", !pathHasComparison);
      }
      continue;
    }
    if (!active) continue;
    const excludedVariant = row.dataset.path === "replace-er-650-er-650r" && ["ER655", "ER655II"].includes(normalizeModel(match.exactModel));
    for (const option of row.querySelectorAll("[data-upgrade-model]")) {
      const intendedFor = (option.dataset.upgradeOldModels || "").split("|").filter(Boolean).map(normalizeModel);
      const oldCode = normalizeModel(match.exactModel);
      const wrongLegacyVariant = Boolean(match.exactModel && intendedFor.length && !intendedFor.some((name) => oldCode === name || oldCode.endsWith(name)));
      option.hidden = excludedVariant || wrongLegacyVariant || Boolean(match.selectedCurrentModel && option.dataset.upgradeModel !== match.selectedCurrentModel);
    }
    for (const note of row.querySelectorAll("[data-upgrade-unmatched]")) note.hidden = !excludedVariant;
    row.open = true;
    row.dataset.activeClassification = match.classification;
    row.dataset.selectedCurrentModel = match.selectedCurrentModel || "";
    for (const block of row.querySelectorAll("[data-closest-content]")) {
      block.hidden = !match.showComparison;
    }
    for (const block of row.querySelectorAll("[data-unresolved-content]")) {
      block.hidden = match.showComparison;
    }
    const badge = row.querySelector("[data-active-badge]");
    if (badge) {
      badge.textContent = match.displayClassification || match.classification;
      badge.classList.toggle("replacement-path-v2__badge--closest", match.showComparison);
      badge.classList.toggle("replacement-path-v2__badge--review", !match.showComparison);
    }
  }
}

function bindAnalytics(root) {
  root.addEventListener("toggle", (event) => {
    const path = event.target.closest?.("[data-replacement-path]");
    if (!path?.open) return;
    const comparison = event.target.closest?.("[data-comparison]");
    if (comparison) {
      emit("replacement_comparison_view", {
        path: path.dataset.path,
        legacy_model: path.dataset.legacyLabel,
        evidence_classification: path.dataset.activeClassification || path.dataset.classification,
        selected_current_model: path.dataset.selectedCurrentModel || path.dataset.currentModels || null,
        lead: false,
      });
      return;
    }
    if (event.target === path) {
      emit("replacement_path_open", {
        path: path.dataset.path,
        legacy_model: path.dataset.legacyLabel,
        evidence_classification: path.dataset.activeClassification || path.dataset.classification,
        selected_current_model: path.dataset.selectedCurrentModel || null,
        lead: false,
      });
    }
  }, true);

  root.addEventListener("click", (event) => {
    const target = event.target.closest?.("[data-rc-event]");
    if (!target) return;
    const path = target.closest("[data-replacement-path]");
    emit(target.dataset.rcEvent, {
      path: path?.dataset.path || null,
      legacy_model: path?.dataset.legacyLabel || null,
      evidence_classification: path?.dataset.activeClassification || path?.dataset.classification || null,
      selected_current_model: path?.dataset.selectedCurrentModel || null,
      document_type: target.dataset.documentType || null,
      lead: target.dataset.rcLead === "true",
    });
  });
}

function openHash(root) {
  const id = decodeURIComponent(window.location.hash.slice(1));
  if (!id || id === root.id) return;
  const target = document.getElementById(id);
  if (target?.matches?.("[data-replacement-path]") && root.contains(target)) {
    root.querySelectorAll("[data-replacement-path]").forEach((path) => { path.open = path === target; });
    const focusTarget = () => {
      target.querySelector("summary")?.focus({ preventScroll: true });
      target.scrollIntoView({ block: "start" });
    };
    focusTarget();
    window.setTimeout(focusTarget, 150);
  }
}

function setIntakeModel(root, model) {
  for (const link of root.querySelectorAll("[data-intake-link]")) {
    let url;
    try { url = new URL(link.getAttribute("href"), window.location.origin); } catch (_) { continue; }
    if (!link.dataset.defaultModel) link.dataset.defaultModel = url.searchParams.get("current_pos_system_or_cash_register") || "";
    const value = model || link.dataset.defaultModel;
    if (value) url.searchParams.set("current_pos_system_or_cash_register", value);
    else url.searchParams.delete("current_pos_system_or_cash_register");
    link.setAttribute("href", url.pathname + url.search);
  }
}

export function initFinder(root) {
  if (!root || root.dataset.initialized === "true") return;
  root.dataset.initialized = "true";
  const paths = domPaths(root);
  const form = root.querySelector("[data-finder-form]");
  const input = root.querySelector("[data-model-search]");
  const status = root.querySelector("[data-finder-status]");
  const unmatched = root.querySelector("[data-unmatched]");

  form?.addEventListener("submit", (event) => {
    event.preventDefault();
    if (!String(input.value || "").trim()) {
      setExactState(root, null);
      setIntakeModel(root, "");
      unmatched.hidden = true;
      status.textContent = "";
      return;
    }
    const match = findMatch(input.value, paths);
    setExactState(root, match);
    setIntakeModel(root, String(input.value || "").trim().slice(0, 60));
    unmatched.hidden = Boolean(match);
    const searchEvent = buildSearchEvent(input.value, match);
    emit("replacement_finder_search", searchEvent);
    emit(match ? "replacement_finder_exact_match" : "replacement_finder_no_match", searchEvent);
    status.textContent = match
      ? `${match.exactModel || match.path.element.dataset.legacyLabel}: ${match.displayClassification || match.classification}. The matching path is open below.`
      : `No exact match found for “${input.value}”. Use the help path below.`;
    if (match) {
      match.path.element.querySelector("summary")?.focus({ preventScroll: true });
      match.path.element.scrollIntoView({ block: "start", behavior: "smooth" });
    }
  });

  bindAnalytics(root);
  openHash(root);
  window.addEventListener("hashchange", () => openHash(root));
}

if (typeof document !== "undefined") {
  const boot = () => document.querySelectorAll("#sam4s-replacement-finder").forEach(initFinder);
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", boot);
  else boot();
}


// Exact model replacements are selected by the merchant's old model number.
// A broad family search retains the menu of options, and impact-printer ER-655
// models do not inherit an ER-650 thermal-printer recommendation.
